/**
 * KITCHEN DISPLAY SCREEN (KDS) — Liefersoft-style
 * Shows active orders as cards with item lists, countdown timers, and status actions.
 * Station-based filtering via printer routing config.
 * All data from Firestore in real-time.
 * Fully translated: EN / DE / NL
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box, Typography, Chip, IconButton, Button, LinearProgress, Dialog, DialogTitle,
  DialogContent, DialogActions,
} from '@mui/material';
import {
  CheckCircle, LocalDining, DeliveryDining, TableBar, TakeoutDining,
  Refresh, Timer, DoneAll, Notifications, NotificationsOff,
  ExpandMore, ExpandLess, Restaurant,
} from '@mui/icons-material';
import { supabase } from '../../services/supabaseClient';
import { useLanguage } from '../../contexts/LanguageContext';

// ─── Types ───────────────────────────────────────────────────────
interface KitchenOrder {
  id: string;
  order_number: number;
  customer_name: string;
  order_type: string; // dine_in, takeaway, delivery
  table_number?: string;
  status: string;
  created_at: Date;
  items: KitchenItem[];
  notes?: string;
  total: number;
  rider_name?: string;
}

interface KitchenItem {
  name: string;
  quantity: number;
  category_name: string;
  notes?: string;
  customizations?: any;
  addons?: string[];
}

interface StationConfig {
  id: string;
  name: string;
  type: string;
  categories: string[];
}

// ─── Constants ───────────────────────────────────────────────────
const ORDER_TARGET_MINUTES = 30; // Default target prep time

const ORDER_TYPE_ICONS: Record<string, React.ReactNode> = {
  dine_in: <TableBar sx={{ fontSize: 18 }} />,
  takeaway: <TakeoutDining sx={{ fontSize: 18 }} />,
  delivery: <DeliveryDining sx={{ fontSize: 18 }} />,
};

// ─── Helpers ─────────────────────────────────────────────────────
const fmtTime = (d: Date) =>
  `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;

const getElapsedMinutes = (created: Date) =>
  Math.max(0, Math.floor((Date.now() - created.getTime()) / 60000));

const getRemainingMinutes = (created: Date, targetMin: number) =>
  Math.max(0, targetMin - getElapsedMinutes(created));

const getProgressPercent = (created: Date, targetMin: number) => {
  const elapsed = getElapsedMinutes(created);
  return Math.min(100, (elapsed / targetMin) * 100);
};

const fmtCountdown = (minutes: number): string => {
  if (minutes <= 0) return '0m';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const timerColor = (created: Date, targetMin: number): string => {
  const pct = getProgressPercent(created, targetMin);
  if (pct >= 100) return '#F44336'; // overdue — red
  if (pct >= 75) return '#FF9800';  // warning — orange
  return '#4CAF50';                  // ok — green
};

const statusBgColor = (status: string): string => {
  if (status === 'pending' || status === 'confirmed') return '#FF9800';
  if (status === 'preparing') return '#2196F3';
  if (status === 'ready') return '#4CAF50';
  return '#6b7280';
};

// ─── Order Card Component ────────────────────────────────────────
const OrderCard: React.FC<{
  order: KitchenOrder;
  stationCategories: string[];
  collapsed: boolean;
  onToggleCollapse: () => void;
  onAdvanceStatus: (order: KitchenOrder) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  now: number;
}> = React.memo(({ order, stationCategories, collapsed, onToggleCollapse, onAdvanceStatus, t, now: _now }) => {
  const remaining = getRemainingMinutes(order.created_at, ORDER_TARGET_MINUTES);
  const progress = getProgressPercent(order.created_at, ORDER_TARGET_MINUTES);
  const color = timerColor(order.created_at, ORDER_TARGET_MINUTES);
  const isOverdue = remaining <= 0;
  const isReady = order.status === 'ready';

  // Filter items for this station (or show all if no station filter)
  const stationItems = stationCategories.length > 0
    ? order.items.filter(item => {
        const cat = (item.category_name || '').toLowerCase();
        return stationCategories.some(sc => cat.includes(sc) || sc.includes(cat));
      })
    : order.items;

  // If station filter active and no items match, show all items (fallback)
  const displayItems = stationItems.length > 0 ? stationItems : order.items;

  const orderTypeLabel = order.order_type === 'dine_in'
    ? (order.table_number ? `${t('kitchen.table')} ${order.table_number}` : t('kitchen.dine_in'))
    : order.order_type === 'takeaway' ? t('kitchen.takeaway') : t('kitchen.delivery');

  const statusLabel = isReady ? t('kitchen.all_ready') :
    order.status === 'preparing' ? t('kitchen.preparing') :
    order.status === 'confirmed' ? t('kitchen.confirmed') : t('kitchen.new');

  const nextAction = isReady ? t('kitchen.done') :
    order.status === 'preparing' ? t('kitchen.mark_ready') :
    t('kitchen.start_preparing');

  return (
    <Box
      sx={{
        backgroundColor: '#252838',
        borderRadius: 2,
        border: `2px solid ${isReady ? '#4CAF50' : isOverdue ? '#F44336' : '#3a3d52'}`,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        '&:hover': { boxShadow: `0 0 12px ${isReady ? 'rgba(76,175,80,0.3)' : 'rgba(0, 200, 150,0.15)'}` },
        minWidth: 0,
      }}
    >
      {/* Card Header */}
      <Box
        onClick={onToggleCollapse}
        sx={{
          px: 1.5, py: 1,
          backgroundColor: statusBgColor(order.status),
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Box sx={{ color: '#fff', display: 'flex', alignItems: 'center' }}>
            {ORDER_TYPE_ICONS[order.order_type] || <LocalDining sx={{ fontSize: 18 }} />}
          </Box>
          <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: '#fff' }}>
            №{order.order_number}
          </Typography>
          {order.table_number && (
            <Chip label={`T${order.table_number}`} size="small"
              sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff' }} />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.8)' }}>
            {fmtTime(order.created_at)}
          </Typography>
          {collapsed ? <ExpandMore sx={{ color: '#fff', fontSize: 18 }} /> : <ExpandLess sx={{ color: '#fff', fontSize: 18 }} />}
        </Box>
      </Box>

      {/* Timer Bar */}
      <Box sx={{ px: 1.5, py: 0.6, display: 'flex', alignItems: 'center', gap: 1, backgroundColor: '#1e2139' }}>
        {isReady ? (
          <Chip icon={<CheckCircle sx={{ fontSize: 14 }} />} label={statusLabel} size="small"
            sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, backgroundColor: 'rgba(76,175,80,0.15)', color: '#4CAF50',
              '& .MuiChip-icon': { color: '#4CAF50' } }} />
        ) : (
          <Chip icon={<Timer sx={{ fontSize: 14 }} />} label={statusLabel} size="small"
            sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700,
              backgroundColor: order.status === 'preparing' ? 'rgba(33,150,243,0.15)' : 'rgba(255,152,0,0.15)',
              color: order.status === 'preparing' ? '#64B5F6' : '#FFB74D',
              '& .MuiChip-icon': { color: order.status === 'preparing' ? '#64B5F6' : '#FFB74D' } }} />
        )}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Timer sx={{ fontSize: 14, color }} />
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color }}>
            {isOverdue ? t('kitchen.overdue') : `${fmtCountdown(remaining)} ${t('kitchen.left')}`}
          </Typography>
          <Box sx={{ flex: 1 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 6, borderRadius: 3,
                backgroundColor: 'rgba(255,255,255,0.08)',
                '& .MuiLinearProgress-bar': { backgroundColor: color, borderRadius: 3 },
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Items (collapsible) */}
      {!collapsed && (
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {displayItems.map((item, i) => (
            <Box key={i} sx={{
              px: 1.5, py: 0.8,
              borderBottom: i < displayItems.length - 1 ? '1px solid #2a2d3e' : 'none',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.02)' },
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Typography sx={{ fontSize: { xs: '1rem', md: '0.92rem' }, fontWeight: 600, color: '#e0e0e0', flex: 1 }}>
                  {item.name}
                </Typography>
                <Typography sx={{ fontSize: { xs: '1rem', md: '0.92rem' }, fontWeight: 800, color: '#e0e0e0', ml: 1 }}>
                  x{item.quantity}
                </Typography>
              </Box>
              {/* Special notes / customizations */}
              {item.notes && (
                <Typography sx={{ fontSize: '0.75rem', color: '#FF9800', fontStyle: 'italic', mt: 0.2, pl: 0.5,
                  borderLeft: '2px solid #FF9800' }}>
                  {item.notes}
                </Typography>
              )}
              {item.addons && item.addons.length > 0 && (
                <Typography sx={{ fontSize: '0.72rem', color: '#64B5F6', mt: 0.2, pl: 0.5,
                  borderLeft: '2px solid #64B5F6' }}>
                  + {item.addons.join(', ')}
                </Typography>
              )}
            </Box>
          ))}
          {order.notes && (
            <Box sx={{ px: 1.5, py: 0.8, borderTop: '1px solid #2a2d3e', backgroundColor: 'rgba(255,152,0,0.05)' }}>
              <Typography sx={{ fontSize: '0.72rem', color: '#FF9800', fontWeight: 600 }}>
                📝 {order.notes}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Action Button — large touch target on mobile */}
      {!collapsed && (
        <Box sx={{ px: 1.5, py: { xs: 1.2, md: 1 }, borderTop: '1px solid #2a2d3e' }}>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: { xs: '0.75rem', md: '0.68rem' }, color: '#6b7280' }}>
              {orderTypeLabel} · {displayItems.length} {t('kitchen.items')}
              {order.rider_name ? ` · 🚗 ${order.rider_name}` : ''}
            </Typography>
            <Button
              variant="contained"
              startIcon={isReady ? <DoneAll sx={{ fontSize: { xs: 18, md: 14 } }} /> : <CheckCircle sx={{ fontSize: { xs: 18, md: 14 } }} />}
              onClick={(e) => { e.stopPropagation(); onAdvanceStatus(order); }}
              sx={{
                textTransform: 'none', fontWeight: 700,
                fontSize: { xs: '0.85rem', md: '0.72rem' },
                borderRadius: 2,
                py: { xs: 0.8, md: 0.4 },
                px: { xs: 2.5, md: 1.5 },
                minWidth: { xs: 90, md: 0 },
                minHeight: { xs: 40, md: 'auto' },
                backgroundColor: isReady ? '#4CAF50' : order.status === 'preparing' ? '#FF9800' : '#2196F3',
                '&:hover': { backgroundColor: isReady ? '#388E3C' : order.status === 'preparing' ? '#e68900' : '#1976D2' },
              }}
            >
              {nextAction}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
});

// ─── Main Component ──────────────────────────────────────────────
const KitchenScreen: React.FC = () => {
  const { t } = useLanguage();
  const [orders, setOrders] = useState<KitchenOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [stations, setStations] = useState<StationConfig[]>([]);
  const [activeStation, setActiveStation] = useState<string>('all');
  const [collapsedOrders, setCollapsedOrders] = useState<Set<string>>(new Set());
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [now, setNow] = useState(Date.now());
  const [confirmDialog, setConfirmDialog] = useState<KitchenOrder | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const prevOrderCountRef = useRef(0);

  // Tick every 30s for timer updates
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(interval);
  }, []);

  // Load station config from Supabase printer_routing
  useEffect(() => {
    const loadStations = async () => {
      try {
        const { data } = await supabase
          .from('printer_routing')
          .select('printers, category_routing')
          .limit(1)
          .maybeSingle();
        if (data) {
          const printers = (data.printers as any[]) || [];
          const catRouting = (data.category_routing as Record<string, any>) || {};

          // Build station list from kitchen printers
          const stationList: StationConfig[] = printers
            .filter((p: any) => p.type === 'kitchen')
            .map((p: any) => {
              const cats: string[] = [];
              for (const [cat, printer] of Object.entries(catRouting)) {
                const pid = Array.isArray(printer) ? (printer as string[])[0] : printer;
                if (pid === p.id) cats.push(cat.toLowerCase());
              }
              return { id: p.id, name: p.name, type: p.type, categories: cats };
            });

          if (stationList.length > 0) {
            setStations(stationList);
            return;
          }
        }
      } catch (e) {
        console.warn('[KDS] Failed to load station config:', e);
      }

      // Fallback defaults
      setStations([
        { id: 'printer1', name: 'Sushi Kitchen', type: 'kitchen', categories: ['sushi', 'maki', 'nigiri', 'bowl', 'sashimi', 'uramaki', 'temaki', 'crispy roll', 'inside out'] },
        { id: 'printer2', name: 'Pizza Kitchen', type: 'kitchen', categories: ['pizza', 'pasta', 'calzone', 'insalata', 'salad'] },
        { id: 'printer3', name: 'Starters', type: 'kitchen', categories: ['starters', 'vorspeisen', 'extras', 'desserts', 'getränke'] },
      ]);
    };
    loadStations();
  }, []);

  // Real-time order listener — active orders only
  useEffect(() => {
    const startOfDay = new Date();
    startOfDay.setHours(6, 0, 0, 0);
    if (new Date().getHours() < 6) startOfDay.setDate(startOfDay.getDate() - 1);

    // Supabase reload + realtime subscription. Pulls the active-pipeline
    // orders (pending → ready) created today, joins their items, and shapes
    // them into the KitchenOrder local type.
    let cancelled = false;

    const reload = async () => {
      const [ordersRes, itemsRes] = await Promise.all([
        supabase
          .from('orders')
          .select('id, order_number, customer_snapshot, type, status, grand_total, notes, placed_at, created_at')
          .in('status', ['pending', 'confirmed', 'preparing', 'ready'])
          .gte('created_at', startOfDay.toISOString())
          .order('created_at', { ascending: true }),
        supabase
          .from('order_items')
          .select('order_id, menu_item_snapshot, quantity, notes, station'),
      ]);

      if (cancelled) return;
      const orders = (ordersRes.data ?? []) as any[];
      const itemsByOrder = new Map<string, any[]>();
      (itemsRes.data ?? []).forEach((it: any) => {
        if (!itemsByOrder.has(it.order_id)) itemsByOrder.set(it.order_id, []);
        itemsByOrder.get(it.order_id)!.push(it);
      });

      const list: KitchenOrder[] = orders.map((row) => {
        const customer = row.customer_snapshot || {};
        return {
          id: row.id,
          order_number: row.order_number || 0,
          customer_name: customer.name || '',
          order_type: row.type || 'dine_in',
          table_number: customer.table_number || '',
          status: row.status || 'pending',
          created_at: new Date(row.placed_at || row.created_at),
          items: (itemsByOrder.get(row.id) || []).map((it: any) => ({
            name: it.menu_item_snapshot?.name || 'Item',
            quantity: it.quantity || 1,
            category_name: it.station || '',
            notes: it.notes || '',
            addons: [],
          })) as KitchenItem[],
          notes: row.notes || '',
          total: Number(row.grand_total) || 0,
          rider_name: '',
        };
      });

      // Play sound for new orders
      if (soundEnabled && list.length > prevOrderCountRef.current && prevOrderCountRef.current > 0) {
        try {
          if (!audioRef.current) {
            audioRef.current = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVghr24teleNjV3teleNjV3k7m1nXlUUXaLp6F7Tk1QbI2kn31TSlNheIqakHBFRVVriZ6YdEVDVm6Ln5l2R0VXZY2hl3RFTF1mjqSXc0NMY2mUqpZxQU1nbJiqo3FDVG9voaykc0NScnWlsqlzRFN1d6q2q3RFVHh5rLqsc0RVenmuurF0R1d8fLK+sXRIV358s8CydEdYgH60wrR1Sll/gLXEs3VLW4CBt8a1dUxdg4K4x7Z1TV6Dg7nJt3VOX4OEu8u5d05ghIW7zLl3T2GFhbzNundQYoaGvc67eFBjh4a+0Lx4UGSIh7/RvXlRZIiHwNO+eVFlioiA1L95UWaKiYHWwHlSZ4uJgtfBelJojImC2MN6U2mNioLZxHtTaY6Kg9rFe1Rqj4uD28d7VGuPi4TcyHxUbJCMhN3JfFVtkYyF3sp8Vm6SjIXfzH1Wb5OMht/NfVdwlI2G4M59V3GUjYfhz31YcZWNh+HQflhylY6H4tF+WXOWjojj0n5Zc5aPiOTTf1p0lo+I5NR/WnSXj4jl1X9bdZiQieXWgFx2mJCJ5teBXHaZkYnn2IFdd5qRiunZgl13mpKK6dqCXnibkorp24Jfe5yTi+vcg197nJOL7N2DYHydk4zt3oNhfZ6Uje3fg2F+npSN7uCEYn+flY3u4YRjf5+Vju/ihGSAoJaO8OOFZIChlY/x5IVlgaGWj/LlhWaCopaQ8uaGZoOjl5Dz54ZnhKOYkfToh2eFpJiR9OmIaIWkmZH16ohohqWZkvbriWmGppmS9+yJaoenmZP37YlriKeZk/juim2Ip5qU+e+Kb4momJT68Ipwi6mZlfvxinGLqpmV/PKLcoyqmpb984tyjaualvz0i3ONq5uX/fWMdI6rm5f+9ox1j6ucmP73jXaQrJyY//iNd5GsnJkA+Y14kq2dmQD6jnmTrZ2ZAPuOepSunpkB/I97la+emgH9j3yWr5+aAv6QfZewnpoC/5B+mbGgmwMAkX+ZsaCbAwGSgJqyoZwEApKBm7OinAQDk4KctKOdBQSUg520o50FBZSEnbWknQYGlYWetaWeBwaWhr+2pp8IB5eGv7emn');
          }
          audioRef.current.play().catch(() => {});
        } catch (e) { /* ignore */ }
      }
      prevOrderCountRef.current = list.length;

      setOrders(list);
      setLoading(false);
    };

    void reload();
    const channel = supabase
      .channel('kds_orders')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, () => { void reload(); })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'order_items' }, () => { void reload(); })
      .subscribe();

    return () => {
      cancelled = true;
      void supabase.removeChannel(channel);
    };
  }, [soundEnabled]);

  // ─── Actions ────────────────────────────────────────────────
  const advanceStatus = useCallback(async (order: KitchenOrder) => {
    const nextStatus =
      order.status === 'ready' ? 'completed' :
      order.status === 'preparing' ? 'ready' :
      'preparing';

    // For completing (removing from screen), show confirm dialog
    if (order.status === 'ready') {
      setConfirmDialog(order);
      return;
    }

    try {
      const patch: Record<string, any> = {
        status: nextStatus,
        updated_at: new Date().toISOString(),
      };
      if (nextStatus === 'ready')     patch.ready_at     = new Date().toISOString();
      if (nextStatus === 'preparing') patch.preparing_at = new Date().toISOString();
      const { error } = await supabase.from('orders').update(patch).eq('id', order.id);
      if (error) throw error;
    } catch (e) { console.error('[KDS] Status update error:', e); }
  }, []);

  const confirmComplete = useCallback(async () => {
    if (!confirmDialog) return;
    try {
      const { error } = await supabase.from('orders').update({
        status: 'completed',
        payment_status: 'completed',
        completed_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }).eq('id', confirmDialog.id);
      if (error) throw error;
    } catch (e) { console.error('[KDS] Complete error:', e); }
    setConfirmDialog(null);
  }, [confirmDialog]);

  const toggleCollapse = useCallback((orderId: string) => {
    setCollapsedOrders(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  }, []);

  // ─── Filtered Orders ───────────────────────────────────────
  const activeStationConfig = stations.find(s => s.id === activeStation);
  const stationCategories = activeStationConfig?.categories || [];

  const filteredOrders = useMemo(() => {
    if (activeStation === 'all') return orders;
    // Show orders that have at least one item matching station categories
    return orders.filter(order =>
      order.items.some(item => {
        const cat = (item.category_name || '').toLowerCase();
        return stationCategories.some(sc => cat.includes(sc) || sc.includes(cat));
      })
    );
  }, [orders, activeStation, stationCategories]);

  // Separate by status for layout
  const readyOrders = filteredOrders.filter(o => o.status === 'ready');
  const preparingOrders = filteredOrders.filter(o => o.status === 'preparing');
  const pendingOrders = filteredOrders.filter(o => o.status === 'pending' || o.status === 'confirmed');

  // Auto-collapse ready orders
  useEffect(() => {
    const readyIds = new Set(readyOrders.map(o => o.id));
    setCollapsedOrders(prev => {
      const next = new Set(prev);
      readyIds.forEach(id => next.add(id));
      return next;
    });
  }, [readyOrders.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Render ─────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: '#1a1d2e', overflow: 'hidden' }}>

      {/* ── Header ──────────────────────────────────────────── */}
      <Box sx={{ px: { xs: 1, md: 2.5 }, py: { xs: 0.8, md: 1 }, flexShrink: 0, borderBottom: '1px solid #3a3d52', backgroundColor: '#1e2139' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
          {/* Title + stats */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Restaurant sx={{ color: '#00C896', fontSize: { xs: 20, md: 22 }, flexShrink: 0 }} />
            <Typography sx={{ fontSize: { xs: '0.95rem', md: '1.1rem' }, fontWeight: 800, color: '#e0e0e0' }} noWrap>
              {activeStation === 'all'
                ? t('kitchen.title')
                : (activeStationConfig?.name || t('kitchen.title'))}
            </Typography>
          </Box>

          {/* Stats & controls */}
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0 }}>
            {pendingOrders.length > 0 && (
              <Chip icon={<Timer sx={{ fontSize: 14 }} />} label={`${pendingOrders.length} ${t('kitchen.new')}`} size="small"
                sx={{ backgroundColor: 'rgba(255,152,0,0.12)', color: '#FF9800', fontWeight: 600, fontSize: '0.72rem',
                  '& .MuiChip-icon': { color: '#FF9800' } }} />
            )}
            {preparingOrders.length > 0 && (
              <Chip icon={<LocalDining sx={{ fontSize: 14 }} />} label={`${preparingOrders.length} ${t('kitchen.preparing')}`} size="small"
                sx={{ backgroundColor: 'rgba(33,150,243,0.12)', color: '#2196F3', fontWeight: 600, fontSize: '0.72rem',
                  '& .MuiChip-icon': { color: '#2196F3' } }} />
            )}
            {readyOrders.length > 0 && (
              <Chip icon={<CheckCircle sx={{ fontSize: 14 }} />} label={`${readyOrders.length} ${t('kitchen.ready')}`} size="small"
                sx={{ backgroundColor: 'rgba(76,175,80,0.12)', color: '#4CAF50', fontWeight: 600, fontSize: '0.72rem',
                  '& .MuiChip-icon': { color: '#4CAF50' } }} />
            )}
            <IconButton size="small" onClick={() => setSoundEnabled(!soundEnabled)}
              sx={{ color: soundEnabled ? '#00C896' : '#6b7280', border: '1px solid #3a3d52', borderRadius: 1.5 }}>
              {soundEnabled ? <Notifications sx={{ fontSize: 18 }} /> : <NotificationsOff sx={{ fontSize: 18 }} />}
            </IconButton>
            <IconButton size="small" onClick={() => window.location.reload()}
              sx={{ color: '#9ca3af', border: '1px solid #3a3d52', borderRadius: 1.5 }}>
              <Refresh sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>
        {/* Station tabs — horizontally scrollable on mobile */}
        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.8, overflow: 'auto', WebkitOverflowScrolling: 'touch', pb: 0.3,
          '&::-webkit-scrollbar': { height: 0 } }}>
          <Chip
            label={t('kitchen.all_stations')}
            size="small"
            onClick={() => setActiveStation('all')}
            sx={{
              height: { xs: 30, md: 26 }, fontSize: { xs: '0.78rem', md: '0.72rem' }, fontWeight: 600, cursor: 'pointer', flexShrink: 0,
              backgroundColor: activeStation === 'all' ? '#00C896' : '#252838',
              color: activeStation === 'all' ? '#1a1d2e' : '#9ca3af',
              border: activeStation === 'all' ? 'none' : '1px solid #3a3d52',
            }}
          />
          {stations.map(station => (
            <Chip
              key={station.id}
              label={station.name}
              size="small"
              onClick={() => setActiveStation(station.id)}
              sx={{
                height: { xs: 30, md: 26 }, fontSize: { xs: '0.78rem', md: '0.72rem' }, fontWeight: 600, cursor: 'pointer', flexShrink: 0,
                backgroundColor: activeStation === station.id ? '#00C896' : '#252838',
                color: activeStation === station.id ? '#1a1d2e' : '#9ca3af',
                border: activeStation === station.id ? 'none' : '1px solid #3a3d52',
              }}
            />
          ))}
        </Box>
      </Box>

      {/* ── Order Grid ──────────────────────────────────────── */}
      <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 1, md: 2 }, WebkitOverflowScrolling: 'touch' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
            <Box sx={{ textAlign: 'center' }}>
              <LocalDining sx={{ fontSize: 48, color: '#3a3d52', mb: 1, animation: 'spin 2s linear infinite',
                '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }} />
              <Typography sx={{ color: '#6b7280', fontSize: '0.9rem' }}>{t('kitchen.loading')}</Typography>
            </Box>
          </Box>
        ) : filteredOrders.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
            <Box sx={{ textAlign: 'center' }}>
              <CheckCircle sx={{ fontSize: 64, color: '#4CAF50', mb: 1, opacity: 0.6 }} />
              <Typography sx={{ color: '#e0e0e0', fontSize: '1.1rem', fontWeight: 600 }}>{t('kitchen.all_done')}</Typography>
              <Typography sx={{ color: '#6b7280', fontSize: '0.82rem', mt: 0.5 }}>{t('kitchen.no_orders_hint')}</Typography>
            </Box>
          </Box>
        ) : (
          <>
            {/* Ready orders row (collapsed by default, green) */}
            {readyOrders.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#4CAF50', textTransform: 'uppercase', mb: 0.8, px: 0.5, letterSpacing: 0.5 }}>
                  ✓ {t('kitchen.ready')} ({readyOrders.length})
                </Typography>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)', xl: 'repeat(5, 1fr)' },
                  gap: 1.5,
                }}>
                  {readyOrders.map(order => (
                    <OrderCard key={order.id} order={order} stationCategories={stationCategories}
                      collapsed={collapsedOrders.has(order.id)} onToggleCollapse={() => toggleCollapse(order.id)}
                      onAdvanceStatus={advanceStatus} t={t} now={now} />
                  ))}
                </Box>
              </Box>
            )}

            {/* Separator */}
            {readyOrders.length > 0 && (pendingOrders.length > 0 || preparingOrders.length > 0) && (
              <Box sx={{ mb: 2, mt: 1 }}>
                <LinearProgress variant="determinate" value={100}
                  sx={{ height: 3, borderRadius: 2, backgroundColor: '#2a2d3e',
                    '& .MuiLinearProgress-bar': { backgroundColor: '#2196F3' } }} />
              </Box>
            )}

            {/* Active orders (pending + preparing, expanded) */}
            {(pendingOrders.length > 0 || preparingOrders.length > 0) && (
              <Box>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#FF9800', textTransform: 'uppercase', mb: 0.8, px: 0.5, letterSpacing: 0.5 }}>
                  🔥 {t('kitchen.in_progress')} ({pendingOrders.length + preparingOrders.length})
                </Typography>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(3, 1fr)', xl: 'repeat(4, 1fr)' },
                  gap: 1.5,
                }}>
                  {[...pendingOrders, ...preparingOrders].map(order => (
                    <OrderCard key={order.id} order={order} stationCategories={stationCategories}
                      collapsed={collapsedOrders.has(order.id)} onToggleCollapse={() => toggleCollapse(order.id)}
                      onAdvanceStatus={advanceStatus} t={t} now={now} />
                  ))}
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* ─── Complete Confirm Dialog ─────────────────────────── */}
      <Dialog open={!!confirmDialog} onClose={() => setConfirmDialog(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3, backgroundColor: '#252838', border: '1px solid #3a3d52' } }}>
        {confirmDialog && (
          <>
            <DialogTitle>
              <Typography sx={{ fontWeight: 700, color: '#e0e0e0' }}>
                {t('kitchen.confirm_complete', { number: String(confirmDialog.order_number) })}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Typography sx={{ color: '#9ca3af', fontSize: '0.88rem' }}>
                {t('kitchen.confirm_complete_desc')}
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
              <Button onClick={() => setConfirmDialog(null)} variant="outlined"
                sx={{ borderColor: '#3a3d52', color: '#9ca3af', textTransform: 'none' }}>{t('kitchen.cancel')}</Button>
              <Button onClick={confirmComplete} variant="contained"
                sx={{ backgroundColor: '#4CAF50', textTransform: 'none', fontWeight: 600, '&:hover': { backgroundColor: '#388E3C' } }}>
                {t('kitchen.confirm')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default KitchenScreen;
